import React from 'react';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { UploadGameImportAsync, StreamGameImportAsync } from '../services/games';
import { GameImportMetadata } from '../types/responses/game-import-response';
import { useToast } from '../contexts/toast-context';
import { appStore } from '../stores/app-store';

export const useGameImport = () => {
  const { onToastOpen } = useToast();
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const [metadata, setMetadata] = React.useState<GameImportMetadata | null>(null);
  const [importToken, setImportToken] = React.useState<string | null>(null);

  const [showPreview, setShowPreview] = React.useState(false);
  const [showProgress, setShowProgress] = React.useState(false);

  const [progressInfo, setProgressInfo] = React.useState<{
    current: number;
    total: number;
    message: string;
    phase: 'uploading' | 'processing';
  }>({ current: 0, total: 0, message: '', phase: 'uploading' });

  const abortControllerRef = React.useRef<AbortController | null>(null);

  const triggerImportFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>, onSuccessCallback?: (newGameId: number) => void) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      appStore.showLoading();
      const response = await UploadGameImportAsync(file);
      setMetadata(response.metadata);
      setImportToken(response.importToken);
      setShowPreview(true);
    } catch (err: any) {
      console.error(err);
      onToastOpen({
        style: 'error',
        title: 'Import Game',
        body: <div>{err.message || 'Failed to read package file. Please make sure it is a valid game ZIP.'}</div>
      });
    } finally {
      appStore.hideLoading();
    }
  };

  const cancelImport = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setShowPreview(false);
    setShowProgress(false);
    setMetadata(null);
    setImportToken(null);
  };

  const confirmImport = async (onSuccessCallback?: (newGameId: number) => void) => {
    if (!importToken) return;

    setShowPreview(false);
    setProgressInfo({ current: 0, total: 100, message: 'Initiating import...', phase: 'processing' });
    setShowProgress(true);

    try {
      const controller = new AbortController();
      abortControllerRef.current = controller;

      await StreamGameImportAsync(
        importToken,
        (event) => {
          if (event.status === 'processing') {
            setProgressInfo({
              current: event.current ?? 0,
              total: event.total ?? 100,
              message: event.message ?? 'Processing...',
              phase: 'processing'
            });
          } else if (event.status === 'completed') {
            setShowProgress(false);
            setMetadata(null);
            setImportToken(null);
            abortControllerRef.current = null;
            
            if (onSuccessCallback) {
              onSuccessCallback(event.newGameId ?? 0);
            }
          } else if (event.status === 'error') {
            throw new Error(event.error || 'Failed to import game.');
          }
        },
        controller.signal
      );
    } catch (err: any) {
      if (err.name === 'AbortError') {
        onToastOpen({
          style: 'info',
          title: 'Import Game',
          body: <div>Import process cancelled.</div>
        });
        return;
      }

      console.error(err);
      setShowProgress(false);
      onToastOpen({
        style: 'error',
        title: 'Import Game',
        body: <div>{err.message || 'Failed to import game. Please try again.'}</div>
      });
    }
  };

  React.useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const renderImportDialogs = (onSuccessCallback: (newGameId:number) => void) => {
    return (
      <>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept=".zip"
          onChange={(e) => onFileChange(e, onSuccessCallback)}
        />

        {showPreview && metadata && (
          <Dialog
            title={<span className="fw-bold text-primary">Import Game Template</span>}
            onClose={cancelImport}
            width={480}>
            <div className="p-3">
              <p className="text-muted mb-3 small">Review the metadata of the game package before importing it to your library:</p>
              
              <div className="card border-0 bg-light p-3 mb-3 rounded-3">
                <div className="mb-2">
                  <span className="text-secondary small d-block" style={{ fontSize: '0.75rem', fontWeight: 600 }}>Game name</span>
                  <strong className="text-dark fs-5">{metadata.originalGameName}</strong>
                </div>
                <hr className="my-2 text-muted" />
                <div className="row">
                  <div className="col-6 mb-2">
                    <span className="text-secondary small d-block" style={{ fontSize: '0.75rem', fontWeight: 600 }}>Organisation</span>
                    <span className="text-dark fw-semibold text-truncate d-block" title={metadata.sourceOrganisation}>{metadata.sourceOrganisation}</span>
                  </div>
                  <div className="col-6 mb-2">
                    <span className="text-secondary small d-block" style={{ fontSize: '0.75rem', fontWeight: 600 }}>Exported by</span>
                    <span className="text-dark fw-semibold text-truncate d-block" title={metadata.exportedBy}>{metadata.exportedBy}</span>
                  </div>
                  <div className="col-6">
                    <span className="text-secondary small d-block" style={{ fontSize: '0.75rem', fontWeight: 600 }}>Export date</span>
                    <span className="text-dark fw-semibold">{metadata.exportDate ? new Date(metadata.exportDate).toLocaleDateString() : '-'}</span>
                  </div>
                  <div className="col-6">
                    <span className="text-secondary small d-block" style={{ fontSize: '0.75rem', fontWeight: 600 }}>Version</span>
                    <span className="badge bg-secondary">v{metadata.sourceVersion}</span>
                  </div>
                </div>
              </div>

              <div className="alert alert-info py-2 px-3 small border-0 rounded-3 mb-4 d-flex align-items-center">
                <span className="material-symbols-outlined me-2 text-info" style={{ fontSize: '18px' }}>info</span>
                <span>The game's version will be reset to <strong>0.0.1 (Draft)</strong> on import.</span>
              </div>

              <div className="d-flex justify-content-end gap-2">
                <Button onClick={cancelImport}>Cancel</Button>
                <Button onClick={() => confirmImport(onSuccessCallback)} themeColor="primary">Confirm Import</Button>
              </div>
            </div>
          </Dialog>
        )}

        {showProgress && (
          <Dialog
            title={<span className="fw-bold text-primary">Importing Game...</span>}
            onClose={cancelImport}
            closeIcon={false}
            width={450}>
            <div className="p-3 text-center">
              <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="fw-semibold text-dark mb-2">{progressInfo.message}</p>
              
              {progressInfo.total > 0 && (
                <div className="w-100 mt-3">
                  <div className="progress mb-2" style={{ height: '12px', borderRadius: '6px' }}>
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated bg-success"
                      role="progressbar"
                      style={{ width: `${(progressInfo.current / progressInfo.total) * 100}%` }}
                      aria-valuenow={progressInfo.current}
                      aria-valuemin={0}
                      aria-valuemax={progressInfo.total}
                    />
                  </div>
                  <small className="text-muted fw-medium">
                    Processed {progressInfo.current}%
                  </small>
                </div>
              )}
            </div>
          </Dialog>
        )}
      </>
    );
  };

  return {
    triggerImportFileSelect,
    renderImportDialogs
  };
};
